/**
 * Professional Voice Speech-to-Text Service
 * Handles Web Speech API with robust error handling and browser compatibility
 * 
 * @author Software Engineering Team
 * @version 1.0.0
 */

import VoicePermissionManager from './VoicePermissionManager';
import VoiceErrorHandler from './VoiceErrorHandler';

class VoiceSTTService {
  constructor() {
    this.recognition = null;
    this.isRecording = false;
    this.isInitialized = false;
    this.permissionManager = new VoicePermissionManager();
    this.errorHandler = new VoiceErrorHandler();
    
    // Configuration
    this.config = {
      language: 'en-US',
      continuous: false,
      interimResults: true,
      maxAlternatives: 1,
      timeout: 30000, // 30 seconds max recording
      confidenceThreshold: 0.5 // Lower threshold for auto-accept
    };
    
    // Event callbacks
    this.callbacks = {
      onStart: null,
      onResult: null,
      onEnd: null,
      onError: null,
      onInterim: null
    };
    
    this.initialize();
  }

  /**
   * Initialize the speech recognition service
   * @returns {Promise<boolean>} - Success status
   */
  async initialize() {
    try {
      // Check browser compatibility
      if (!this.permissionManager.isSpeechRecognitionSupported()) {
        throw new Error('Speech recognition not supported in this browser');
      }

      // Get the appropriate SpeechRecognition constructor
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      if (!SpeechRecognition) {
        throw new Error('Speech recognition API not available');
      }

      this.recognition = new SpeechRecognition();
      this.configureRecognition();
      this.setupEventListeners();
      
      this.isInitialized = true;
      console.info('VoiceSTTService initialized successfully');
      return true;
      
    } catch (error) {
      console.error('Failed to initialize VoiceSTTService:', error);
      this.errorHandler.handleError(error, 'INITIALIZATION_ERROR');
      return false;
    }
  }

  /**
   * Configure speech recognition settings
   * @private
   */
  configureRecognition() {
    if (!this.recognition) return;

    this.recognition.lang = this.config.language;
    this.recognition.continuous = this.config.continuous;
    this.recognition.interimResults = this.config.interimResults;
    this.recognition.maxAlternatives = this.config.maxAlternatives;
    
    // Optimize for better user experience
    if ('webkitSpeechRecognition' in window) {
      // Chrome/Edge specific optimizations
      this.recognition.continuous = true; // Better for continuous speech
      this.recognition.interimResults = true; // Show partial results
    }
  }

  /**
   * Setup event listeners for speech recognition
   * @private
   */
  setupEventListeners() {
    if (!this.recognition) return;

    this.recognition.onstart = () => {
      this.isRecording = true;
      console.info('Speech recognition started');
      this.callbacks.onStart?.();
    };

    this.recognition.onresult = (event) => {
      this.handleRecognitionResult(event);
    };

    this.recognition.onend = () => {
      this.isRecording = false;
      console.info('Speech recognition ended');
      this.callbacks.onEnd?.();
    };

    this.recognition.onerror = (event) => {
      this.isRecording = false;
      const error = this.errorHandler.parseRecognitionError(event);
      console.error('Speech recognition error:', error);
      this.callbacks.onError?.(error);
    };

    this.recognition.onnomatch = () => {
      const error = { 
        type: 'NO_MATCH', 
        message: 'No speech was recognized. Please try speaking more clearly.' 
      };
      this.callbacks.onError?.(error);
    };

    this.recognition.onspeechend = () => {
      console.info('Speech ended');
    };
  }

  /**
   * Handle recognition results
   * @param {SpeechRecognitionEvent} event - Recognition event
   * @private
   */
  handleRecognitionResult(event) {
    const result = event.results[event.results.length - 1];
    const transcript = result[0].transcript;
    const confidence = result[0].confidence;
    const isFinal = result.isFinal;

    console.info(`Recognition result: "${transcript}" (confidence: ${confidence}, final: ${isFinal})`);

    if (isFinal) {
      // Final result - always use it if it has any reasonable content
      if (transcript.trim().length > 0 && confidence >= this.config.confidenceThreshold) {
        this.callbacks.onResult?.({
          transcript: transcript.trim(),
          confidence,
          isFinal: true
        });
      } else if (transcript.trim().length > 0) {
        // Lower confidence but still use it - better UX than asking
        console.info(`Using lower confidence result: "${transcript}" (${confidence})`);
        this.callbacks.onResult?.({
          transcript: transcript.trim(),
          confidence,
          isFinal: true
        });
      }
    } else {
      // Interim result - show real-time typing
      this.callbacks.onInterim?.({
        transcript: transcript.trim(),
        confidence,
        isFinal: false
      });
    }
  }

  /**
   * Start speech recognition
   * @returns {Promise<void>}
   */
  async startRecording() {
    try {
      if (!this.isInitialized) {
        const initialized = await this.initialize();
        if (!initialized) {
          throw new Error('Failed to initialize speech recognition');
        }
      }

      if (this.isRecording) {
        console.warn('Recording is already in progress');
        return;
      }

      // Check and request microphone permission
      const hasPermission = await this.permissionManager.requestMicrophonePermission();
      if (!hasPermission) {
        throw new Error('Microphone permission denied');
      }

      // Start recognition with timeout
      this.recognition.start();
      
      // Set timeout to auto-stop after configured time
      setTimeout(() => {
        if (this.isRecording) {
          this.stopRecording();
        }
      }, this.config.timeout);

    } catch (error) {
      console.error('Failed to start recording:', error);
      this.errorHandler.handleError(error, 'START_RECORDING_ERROR');
      this.callbacks.onError?.(this.errorHandler.parseError(error));
    }
  }

  /**
   * Stop speech recognition
   */
  stopRecording() {
    try {
      if (this.recognition && this.isRecording) {
        this.recognition.stop();
        console.info('Speech recognition stopped manually');
      }
    } catch (error) {
      console.error('Error stopping recognition:', error);
      this.errorHandler.handleError(error, 'STOP_RECORDING_ERROR');
    }
  }

  /**
   * Abort speech recognition immediately
   */
  abortRecording() {
    try {
      if (this.recognition && this.isRecording) {
        this.recognition.abort();
        this.isRecording = false;
        console.info('Speech recognition aborted');
      }
    } catch (error) {
      console.error('Error aborting recognition:', error);
    }
  }

  /**
   * Set event callbacks
   * @param {Object} callbacks - Event callbacks
   */
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Update configuration
   * @param {Object} newConfig - New configuration options
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    if (this.recognition) {
      this.configureRecognition();
    }
  }

  /**
   * Get current status
   * @returns {Object} - Service status
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isRecording: this.isRecording,
      isSupported: this.permissionManager.isSpeechRecognitionSupported(),
      config: this.config
    };
  }

  /**
   * Cleanup resources
   */
  destroy() {
    if (this.recognition) {
      this.abortRecording();
      this.recognition = null;
    }
    this.isInitialized = false;
    this.callbacks = {};
    console.info('VoiceSTTService destroyed');
  }
}

export default VoiceSTTService;
